import { useState, useRef } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import html2pdf from "html2pdf.js";
import { orders } from "@/mocks/orders";

export default function InvoicePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const order = orders.find((o) => o.id === id);

  const invoiceRef = useRef<HTMLDivElement>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  if (!order) {
    return <Navigate to="/provider/orders" replace />;
  }

  const amountNum = Number(order.amount) || 0;
  const commission = order.commission ?? (Math.round(amountNum * 0.15 * 100) / 100).toFixed(2);
  const net = order.net ?? (Math.round(amountNum * 0.85 * 100) / 100).toFixed(2);
  const completedDate = order.completedDate ?? order.date ?? order.schedule;

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    const el = invoiceRef.current;
    if (!el || generatingPdf) return;
    setGeneratingPdf(true);
    try {
      await html2pdf()
        .set({
          margin: [10, 10, 12, 10],
          filename: `invoice-${order.id}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .from(el)
        .save();
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[820px]">
      {/* Back + actions */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <button
          type="button"
          onClick={() => navigate(`/provider/orders/${order.id}`)}
          className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-ghasla-text-secondary transition-colors hover:text-ghasla-text-link"
        >
          <i className="ri-arrow-right-line text-lg" />
          العودة لتفاصيل الطلب
        </button>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handlePrint}
            className="flex h-11 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-[#E4F5F5] bg-white px-5 text-sm font-semibold text-ghasla-text-link transition-colors hover:bg-[#F0FDFD]"
          >
            <i className="ri-printer-line" />
            طباعة
          </button>
          <button
            type="button"
            onClick={handleDownloadPDF}
            disabled={generatingPdf}
            className="flex h-11 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-[#00AAB7] px-5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {generatingPdf ? (
              <i className="ri-loader-4-line animate-spin" />
            ) : (
              <i className="ri-download-2-line" />
            )}
            {generatingPdf ? "جارٍ التحضير..." : "تحميل PDF"}
          </button>
        </div>
      </div>

      {/* Invoice card */}
      <div className="overflow-hidden rounded-[20px] border border-[#F0F4F4] bg-white shadow-card">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#F0F4F4] bg-[#F4FAFA] px-6 py-5 md:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl bg-[#007A85]">
              <img
                src="https://storage.helloreaddy.io/project_files/9818337a-0a98-464f-85b4-cd986046343e/9ac90c09-7871-46ab-ad1d-a8594706691c_compressed_ChatGPT-Image-2--2026-11_08_10--1.webp"
                alt="غسلة"
                className="h-7 w-auto object-contain"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-ghasla-text-heading">فاتورة غسلة</h1>
              <p className="text-xs text-ghasla-text-secondary">رقم الطلب: #{order.id}</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E8F6F6] px-3 py-1 text-xs font-medium text-ghasla-text-link">
            <i className="ri-file-text-line text-sm" />
            فاتورة ضريبية مبسطة
          </span>
        </div>

        {/* Body */}
        <div className="px-6 py-6 md:px-8">
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-4 py-2">
              <span className="shrink-0 text-sm text-ghasla-text-secondary">تاريخ التنفيذ</span>
              <span className="text-sm font-semibold text-ghasla-text-heading">{completedDate}</span>
            </div>
            <div className="flex items-center justify-between gap-4 py-2">
              <span className="shrink-0 text-sm text-ghasla-text-secondary">العميل</span>
              <span className="text-sm font-semibold text-ghasla-text-heading">{order.customer}</span>
            </div>
            <div className="flex items-center justify-between gap-4 py-2">
              <span className="shrink-0 text-sm text-ghasla-text-secondary">الجوال</span>
              <span dir="ltr" className="text-sm font-medium text-ghasla-text-primary">{order.phone}</span>
            </div>
            <div className="flex items-center justify-between gap-4 py-2">
              <span className="shrink-0 text-sm text-ghasla-text-secondary">الخدمة</span>
              <span className="text-sm font-semibold text-ghasla-text-heading">{order.service}</span>
            </div>
            {order.assignedTech && (
              <div className="flex items-center justify-between gap-4 py-2">
                <span className="shrink-0 text-sm text-ghasla-text-secondary">المنفذ</span>
                <span className="text-sm font-semibold text-ghasla-text-heading">{order.assignedTech}</span>
              </div>
            )}
          </div>

          <div className="my-5 border-t border-dashed border-[#E4F5F5]" />

          <div className="space-y-1">
            <div className="flex items-center justify-between gap-4 py-2">
              <span className="shrink-0 text-sm text-ghasla-text-secondary">إجمالي الطلب</span>
              <span className="text-sm font-semibold text-ghasla-text-heading">{order.amount} ر.س</span>
            </div>
            <div className="flex items-center justify-between gap-4 py-2">
              <span className="shrink-0 text-sm text-ghasla-text-secondary">العمولة (15%)</span>
              <span className="text-sm font-semibold text-[#E74C3C]">- {commission} ر.س</span>
            </div>

            <div className="my-4 border-t border-[#F0F4F4]" />

            <div className="flex items-center justify-between gap-4 rounded-xl bg-[#F0FDFD] px-4 py-3.5">
              <span className="text-base font-semibold text-ghasla-text-heading">صافي المستحق</span>
              <span className="text-xl font-bold text-ghasla-text-link">{net} ر.س</span>
            </div>
          </div>

          {order.rating && (
            <div className="mt-4 rounded-xl bg-[#F4FAFA] p-4">
              <div className="flex items-center gap-1" dir="ltr">
                {[1, 2, 3, 4, 5].map((i) => (
                  <i
                    key={i}
                    className={`ri-star-fill text-sm ${i <= order.rating ? "text-[#F59E0B]" : "text-[#D5E3E3]"}`}
                  />
                ))}
                <span className="mr-1 text-xs text-ghasla-text-secondary">({order.rating}/5)</span>
              </div>
              {order.ratingComment && (
                <p className="mt-1 text-sm text-ghasla-text-secondary">&ldquo;{order.ratingComment}&rdquo;</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#F0F4F4] bg-[#FBFEFE] px-6 py-4 text-center text-xs text-ghasla-text-secondary md:px-8">
          شكراً لاستخدامك غسلة
        </div>
      </div>

      {/* Off-screen invoice for PDF download */}
      <div
        ref={invoiceRef}
        dir="rtl"
        style={{
          position: "fixed",
          left: "-9999px",
          top: "0",
          width: "720px",
          background: "#ffffff",
          padding: "40px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "2px solid #00AAB7",
            paddingBottom: "20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "10px",
                background: "#007A85",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                fontWeight: 700,
              }}
            >
              غسلة
            </div>
            <div>
              <div style={{ fontSize: "20px", fontWeight: 700, color: "#0F172A" }}>فاتورة غسلة</div>
              <div style={{ fontSize: "12px", color: "#6B7280" }}>رقم الطلب: #{order.id}</div>
            </div>
          </div>
          <div style={{ fontSize: "12px", color: "#6B7280" }}>فاتورة ضريبية مبسطة</div>
        </div>

        <div style={{ marginTop: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "14px" }}>
            <span style={{ color: "#6B7280" }}>تاريخ التنفيذ</span>
            <span style={{ fontWeight: 600, color: "#0F172A" }}>{completedDate}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "14px" }}>
            <span style={{ color: "#6B7280" }}>العميل</span>
            <span style={{ fontWeight: 600, color: "#0F172A" }}>{order.customer}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "14px" }}>
            <span style={{ color: "#6B7280" }}>الخدمة</span>
            <span style={{ fontWeight: 600, color: "#0F172A" }}>{order.service}</span>
          </div>
          {order.assignedTech && (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "14px" }}>
              <span style={{ color: "#6B7280" }}>المنفذ</span>
              <span style={{ fontWeight: 600, color: "#0F172A" }}>{order.assignedTech}</span>
            </div>
          )}

          <div style={{ borderTop: "1px dashed #E4F5F5", margin: "16px 0" }} />

          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "14px" }}>
            <span style={{ color: "#6B7280" }}>إجمالي الطلب</span>
            <span style={{ fontWeight: 600, color: "#0F172A" }}>{order.amount} ر.س</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "14px" }}>
            <span style={{ color: "#6B7280" }}>العمولة (15%)</span>
            <span style={{ fontWeight: 600, color: "#E74C3C" }}>- {commission} ر.س</span>
          </div>

          <div style={{ borderTop: "1px solid #E4F5F5", margin: "12px 0" }} />

          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "16px" }}>
            <span style={{ fontWeight: 700, color: "#0F172A" }}>صافي المستحق</span>
            <span style={{ fontWeight: 700, color: "#00AAB7" }}>{net} ر.س</span>
          </div>

          {order.rating && (
            <div
              style={{
                marginTop: "12px",
                background: "#F4FAFA",
                borderRadius: "10px",
                padding: "12px",
                fontSize: "12px",
                color: "#6B7280",
              }}
            >
              تقييم العميل: {order.rating} / 5
              {order.ratingComment ? ` — ${order.ratingComment}` : ""}
            </div>
          )}
        </div>

        <div style={{ marginTop: "32px", textAlign: "center", fontSize: "11px", color: "#9CA3AF" }}>
          شكراً لاستخدامك غسلة
        </div>
      </div>

      {/* Print-only invoice */}
      <div className="hidden print:block">
        <div className="p-8">
          <div className="flex items-center gap-3 border-b border-gray-300 pb-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#007A85]">
              <img
                src="https://storage.helloreaddy.io/project_files/9818337a-0a98-464f-85b4-cd986046343e/9ac90c09-7871-46ab-ad1d-a8594706691c_compressed_ChatGPT-Image-2--2026-11_08_10--1.webp"
                alt="غسلة"
                className="h-8 w-auto object-contain"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">فاتورة غسلة</h1>
              <p className="text-sm text-gray-500">رقم الطلب: #{order.id}</p>
            </div>
          </div>
          <div className="mt-6 space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">التاريخ</span><span className="font-semibold text-gray-900">{completedDate}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">العميل</span><span className="font-semibold text-gray-900">{order.customer}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">الخدمة</span><span className="font-semibold text-gray-900">{order.service}</span></div>
            {order.assignedTech && <div className="flex justify-between"><span className="text-gray-500">المنفذ</span><span className="font-semibold text-gray-900">{order.assignedTech}</span></div>}
            <div className="my-4 border-t border-dashed border-gray-300" />
            <div className="flex justify-between"><span className="text-gray-500">إجمالي الطلب</span><span className="font-semibold text-gray-900">{order.amount} ر.س</span></div>
            <div className="flex justify-between"><span className="text-gray-500">العمولة (15%)</span><span className="font-semibold text-red-600">- {commission} ر.س</span></div>
            <div className="my-2 border-t border-gray-300" />
            <div className="flex justify-between text-base"><span className="font-bold text-gray-900">صافي المستحق</span><span className="font-bold text-gray-900">{net} ر.س</span></div>
          </div>
          <div className="mt-8 text-center text-xs text-gray-400">
            شكراً لاستخدامك غسلة
          </div>
        </div>
      </div>
    </div>
  );
}